import Stack from '@mui/material/Stack';
import Box from '@mui/material/Box';

export type GalleryItem = {
  id: string;
  url: string;
  name: string;
  showDimensions?: boolean;
};

function Gallery({ children }: { children: React.ReactNode }) {
  return (
    <Box
      component="ol"
      sx={{
        boxSizing: 'border-box', // prevent gap and padding from overflowing
        width: '100%',
        height: '100%',
        overflowY: 'auto',
        padding: 0,
        listStyle: 'none',

        display: 'grid',
        gridTemplateColumns: 'repeat(2, 6.5rem)',
        justifyContent: 'center',
        gap: 1,

        '& > *': {
          width: '6.5rem',
          height: '6.5rem',
          '& > li': {
            border: '1px solid transparent',
          },
          '&:hover > li': {
            bgcolor: 'grey.200',
            border: (theme) => `1px solid ${theme.palette.secondary.main}`,
            borderRadius: 1,
          },
        },
      }}
    >
      {children}
    </Box>
  );
}

function GalleryTile({ children, size = 'medium' }: { children: React.ReactNode; size?: 'small' | 'medium' }) {
  return (
    <Stack
      component="li"
      direction="column"
      gap={0.25}
      width={size === 'small' ? '3.25rem' : '6.5rem'}
      height={size === 'small' ? '3.25rem' : '6.5rem'}
      justifyContent="center"
      alignItems="center"
      borderRadius={1}
      bgcolor="grey.100"
      sx={{
        position: 'relative',
      }}
    >
      {children}
    </Stack>
  );
}

type GalleryTileImageProps =
  | {
      url: string;
      title: string;
      size?: 'small' | 'medium';
      children?: never;
    }
  | {
      url?: never;
      title?: never;
      size?: never;
      children: React.ReactNode;
    };

function GalleryTileImage({ url, title, children, size = 'medium' }: GalleryTileImageProps) {
  return (
    <Box
      component={children ? 'div' : 'img'}
      src={url}
      alt={title}
      loading="lazy"
      sx={{
        marginY: 'auto',
        width: size === 'small' ? '3.25rem' : '6.5rem',
        height: size === 'small' ? '3.25rem' : '6.5rem',
        maxWidth: size === 'small' ? '3.25rem' : '6.5rem',
        maxHeight: size === 'small' ? '3.25rem' : '6.5rem',
        objectFit: 'contain',
        display: 'block', // Ensures image is treated as block element
        borderRadius: 1,
      }}
    >
      {children}
    </Box>
  );
}

export const ImagesLayout = {
  Gallery,
  GalleryTile,
  GalleryTileImage,
};
